import React, { FormEvent, useEffect, useState } from 'react'
import { Box, Button, CircularProgress, Paper, Typography } from '@material-ui/core'
import { Link } from 'react-router-dom'
import logo from '@assets/Logo.png'
import PasswordField from '@views/shared/PasswordField'
import { completePasswordRecovery } from './passwordRecoveryApi'

function getPasswordFeedback(password: string) {
  const missing: string[] = []
  const issues: string[] = []
  if (Array.from(password).length < 8) missing.push('8 caracteres')
  if (!/\p{L}/u.test(password)) missing.push('una letra')
  if (!/\p{N}/u.test(password)) missing.push('un número')
  if (missing.length) issues.push(`Usa al menos: ${missing.join(', ')}.`)
  if (/\s/u.test(password)) issues.push('No uses espacios.')
  if (new TextEncoder().encode(password).length > 72) issues.push('Usa máximo 72 bytes UTF-8.')
  return { issues, valid: issues.length === 0 }
}

const PasswordRecoveryCompleteView: React.FC = () => {
  const [challengeId, setChallengeId] = useState('')
  const [token, setToken] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [loading, setLoading] = useState(false)
  const [complete, setComplete] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1))
    const id = params.get('challenge') || ''
    const secret = params.get('token') || ''
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
    setChallengeId(id)
    setToken(secret)
  }, [])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const feedback = getPasswordFeedback(password)
    if (!feedback.valid) {
      setError(feedback.issues.join(' '))
      return
    }
    if (password !== confirmation) {
      setError('Las contraseñas no coinciden.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await completePasswordRecovery(challengeId, token, password)
      setComplete(true)
      setChallengeId('')
      setToken('')
      setPassword('')
      setConfirmation('')
    } catch (requestError: any) {
      const responseMessage = (requestError as {
        response?: { data?: { message?: string } }
      })?.response?.data?.message
      setError(responseMessage || 'El enlace no es válido o venció. Solicita uno nuevo.')
    } finally {
      setLoading(false)
    }
  }

  const hasLink = Boolean(challengeId && token)
  const passwordFeedback = getPasswordFeedback(password)

  return (
    <div className="center_view">
      <img src={logo} alt="CO Capitalizaciones" style={{ height: '7em' }} />
      <Paper
        elevation={3}
        style={{
          width: 'min(100%, 440px)',
          padding: '32px 28px',
          borderRadius: 16
        }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          Nueva contraseña
        </Typography>
        {complete ? (
          <Typography align="center" color="textSecondary">
            Tu contraseña se actualizó. Inicia sesión con tu nueva contraseña y verifica tu correo.
          </Typography>
        ) : !hasLink ? (
          <Typography align="center" color="textSecondary">
            Este enlace no es válido. Solicita uno nuevo para continuar.
          </Typography>
        ) : (
          <form onSubmit={submit}>
            <Typography color="textSecondary" align="center" paragraph>
              Usa al menos 8 caracteres, una letra y un número. No uses espacios.
            </Typography>
            <PasswordField
              fullWidth
              required
              label="Nueva contraseña"
              autoComplete="new-password"
              margin="normal"
              value={password}
              disabled={loading}
              onChange={event => setPassword(event.target.value)}
            />
            <PasswordField
              fullWidth
              required
              label="Confirmar contraseña"
              autoComplete="new-password"
              margin="normal"
              value={confirmation}
              disabled={loading}
              onChange={event => setConfirmation(event.target.value)}
            />
            {error && (
              <Typography color="error" role="alert">
                {error}
              </Typography>
            )}
            <Box marginTop={3}>
              <Button fullWidth type="submit" variant="contained" color="primary" disabled={loading}>
                {loading ? <CircularProgress size={22} /> : 'Guardar contraseña'}
              </Button>
              <Typography
                align="center"
                color={password && passwordFeedback.valid ? 'primary' : 'textSecondary'}
                variant="body2"
                role="status"
                aria-live="polite"
                style={{ marginTop: 10 }}
              >
                {passwordFeedback.issues.length
                  ? passwordFeedback.issues.join(' ')
                  : password
                  ? 'La contraseña cumple los requisitos.'
                  : 'Usa al menos: 8 caracteres, una letra, un número.'}
              </Typography>
            </Box>
          </form>
        )}
        <Box textAlign="center" marginTop={3}>
          <Link to="/login" style={{ color: '#e65f00', fontWeight: 600 }}>
            Volver a iniciar sesión
          </Link>
        </Box>
      </Paper>
    </div>
  )
}

export default PasswordRecoveryCompleteView
